import { updateTestRunSchema, type TestRunDto } from '@tms/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { RichTextEditor } from '../../components/rich-text';
import {
  Button,
  Dialog,
  Field,
  Input,
  SelectMenu,
  SelectMenuItem,
} from '../../components/ui';
import { useErrorText, useT } from '../../i18n';
import { EnvironmentSelect } from '../environments/EnvironmentSelect';
import { useUsers } from '../users/api';
import { useUpdateTestRun } from './api';

interface EditValues {
  title: string;
  environmentId?: string | null;
  assigneeId?: string | null;
  description?: string | null;
  comment?: string | null;
}

/** Sentinel for the "unassigned" option: a select value is always a string. */
const NONE = 'none';

export const EditRunDialog = ({
  open,
  onOpenChange,
  projectCode,
  run,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectCode: string;
  run: TestRunDto;
}) => {
  const { t } = useT();
  const { errorText, applyApiError } = useErrorText();
  const update = useUpdateTestRun(projectCode, run.id);
  const { data: users } = useUsers({ perPage: 100 }, { enabled: open });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<EditValues>({
    resolver: zodResolver(updateTestRunSchema),
    defaultValues: { title: run.title },
  });

  useEffect(() => {
    if (!open) return;
    reset({
      title: run.title,
      environmentId: run.environment?.id ?? null,
      assigneeId: run.assignee?.id ?? null,
      description: run.description ?? '',
      comment: run.comment ?? '',
    });
  }, [open, run, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      await update.mutateAsync({
        title: values.title,
        environmentId: values.environmentId ?? null,
        assigneeId: values.assigneeId ?? null,
        description: values.description?.trim() ? values.description : null,
        comment: values.comment?.trim() ? values.comment : null,
      });
      toast.success(t('runs.updated'));
      onOpenChange(false);
    } catch (error) {
      if (!applyApiError(error, setError, ['title', 'environmentId', 'assigneeId'])) {
        toast.error(errorText(error, 'runs.updateFailed'));
      }
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('runs.editTitle', { key: run.runKey })}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => void onSubmit()} loading={isSubmitting}>
            {t('common.saveChanges')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <Field label={t('common.title')} error={errors.title?.message} required>
          {({ id, invalid }) => <Input id={id} invalid={invalid} autoFocus {...register('title')} />}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('runs.fieldEnvironment')} error={errors.environmentId?.message}>
            {({ id, invalid }) => (
              <Controller
                control={control}
                name="environmentId"
                render={({ field }) => (
                  <EnvironmentSelect
                    id={id}
                    invalid={invalid}
                    projectCode={projectCode}
                    value={field.value ?? null}
                    onChange={field.onChange}
                    enabled={open}
                  />
                )}
              />
            )}
          </Field>
          <Field label={t('runs.fieldAssignee')} error={errors.assigneeId?.message}>
            {({ id, invalid }) => (
              <Controller
                control={control}
                name="assigneeId"
                render={({ field }) => (
                  <SelectMenu
                    key={field.value ?? NONE}
                    id={id}
                    invalid={invalid}
                    value={field.value ?? NONE}
                    onValueChange={(value) => field.onChange(value === NONE ? null : value)}
                  >
                    <SelectMenuItem value={NONE}>{t('common.unassigned')}</SelectMenuItem>
                    {(users?.data ?? []).map((user) => (
                      <SelectMenuItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectMenuItem>
                    ))}
                  </SelectMenu>
                )}
              />
            )}
          </Field>
        </div>
        <Field label={t('common.description')} error={errors.description?.message}>
          {({ id, invalid }) => (
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <RichTextEditor
                  id={id}
                  invalid={invalid}
                  projectCode={projectCode}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  rows={3}
                />
              )}
            />
          )}
        </Field>
        <Field label={t('common.comment')} error={errors.comment?.message}>
          {({ id, invalid }) => (
            <Controller
              control={control}
              name="comment"
              render={({ field }) => (
                <RichTextEditor
                  id={id}
                  invalid={invalid}
                  projectCode={projectCode}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  rows={3}
                />
              )}
            />
          )}
        </Field>
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
};
